import { Alert, Box, Snackbar, Typography } from "@mui/material";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type QueryClient from "../../api/query/queryClient";
import { BORDER_RADIUS, COLORS, SPACING, TYPOGRAPHY } from "./constants";
import ReviewItem from "./ReviewItem";
import type { ManagerReviewItem } from "./types";

interface ManagerReviewSectionProps {
  reviewItems: ManagerReviewItem[];
  queryClient: QueryClient;
  practiceIds: string[];
  onReload: () => Promise<void>;
}

export default function ManagerReviewSection({
  reviewItems,
  queryClient,
  practiceIds,
  onReload,
}: ManagerReviewSectionProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    const item = reviewItems.find((reviewItem) => reviewItem.id === id);
    if (!item) return;

    try {
      await queryClient.dismissNotification(
        practiceIds,
        item.agent,
        item.templateCode,
      );
      // Reload notifications after dismissing
      await onReload();
    } catch (_error) {
      setError("Failed to dismiss item. Please try again.");
    }
  };

  const handleDoItNow = async (id: string) => {
    const item = reviewItems.find((reviewItem) => reviewItem.id === id);
    if (!item) return;

    try {
      // Call the action API
      await queryClient.handleNotificationAction(
        practiceIds,
        item.agent,
        item.templateCode,
      );

      // Reload notifications after action
      await onReload();

      // Navigate to the link if available
      if (item.link) {
        const currentParams = new URLSearchParams(location.search);
        const devmode = currentParams.get("devmode");

        const [basePath, linkParams] = item.link.split("?");
        const targetParams = new URLSearchParams(linkParams || "");

        if (devmode) {
          targetParams.set("devmode", devmode);
        }

        const newUrl =
          basePath +
          (targetParams.toString() ? `?${targetParams.toString()}` : "");
        navigate(newUrl);
      }
    } catch (_error) {
      setError("Failed to start this action. Please try again.");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        padding: `${SPACING.xxl} ${SPACING.xl}`,
        flexDirection: "column",
        alignItems: "flex-start",
        gap: SPACING.xxl,
        borderRadius: BORDER_RADIUS.lg,
        border: `1px solid ${COLORS.border}`,
        background: COLORS.background.section,
      }}
    >
      {/* Content area */}
      <Box sx={{ width: "100%", flex: 1 }}>
        {/* Section header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            backgroundColor: "transparent",
            padding: `${SPACING.md} ${SPACING.lg}`,
            borderRadius: BORDER_RADIUS.sm,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              ...TYPOGRAPHY.heading.h4,
            }}
          >
            Manager Review
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              padding: "6px 16px",
              alignItems: "center",
              borderRadius: "16px",
              backgroundColor: "#D1FAE5",
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#065F46",
              }}
            >
              {reviewItems.length === 0
                ? "No Items"
                : `${reviewItems.length} ${reviewItems.length === 1 ? "Item" : "Items"}`}
            </Typography>
          </Box>
        </Box>

        {/* Review items list or empty state */}
        {reviewItems.length > 0 ? (
          reviewItems.map((item) => (
            <ReviewItem
              key={item.id}
              id={item.id}
              iconUrl={item.iconUrl}
              heading={item.heading}
              description={item.description}
              count={item.count}
              link={item.link}
              agent={item.agent}
              templateCode={item.templateCode}
              practiceId={item.practiceId}
              onDelete={handleDelete}
              onDoItNow={handleDoItNow}
            />
          ))
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: `${SPACING.xxxl} ${SPACING.xl}`,
              gap: SPACING.lg,
              minHeight: "200px",
            }}
          >
            {/* Star icon */}
            <Box
              sx={{
                fontSize: "64px",
                lineHeight: 1,
              }}
            >
              ⭐
            </Box>

            {/* Empty state message */}
            <Typography
              sx={{
                ...TYPOGRAPHY.heading.h5,
                color: COLORS.text.primary,
                textAlign: "center",
              }}
            >
              You are all caught up!
            </Typography>
          </Box>
        )}
      </Box>

      <Snackbar
        open={error !== null}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
